import Plugin from "@ckeditor/ckeditor5-core/src/plugin";
import { toWidget } from "@ckeditor/ckeditor5-widget/src/utils";
import Widget from "@ckeditor/ckeditor5-widget/src/widget";
import Command from "@ckeditor/ckeditor5-core/src/command";

const activityIconPaths = {
  video: [
    "M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z",
  ],
  image: [
    "M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z",
  ],
  link: [
    "M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z",
  ],
  document: [
    "M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z",
  ],
  page: [
    "M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z",
  ],
  policy_document: [
    "M20 3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H4V5h16v14z",
    "M19.41 10.42L17.99 9l-3.17 3.17-1.41-1.42L12 12.16 14.82 15l4.59-4.58zM10 7H5v2h5V7zm0 4H5v2h5v-2zm0 4H5v2h5v-2z",
  ],
};

const downcastConversion = {
  model: "repositoryItemLink",
  view: (modelElement, { writer: viewWriter }) => {
    const attachableId = modelElement.getAttribute(
      "content-repository-attachable-id"
    );
    const itemType = modelElement.getAttribute("type");
    const itemName = modelElement.getAttribute("name");
    const repositoryItemId = modelElement.getAttribute("repository-item-id");
    const itemPath = modelElement.getAttribute("path");
    const div = viewWriter.createContainerElement("div", {
      class: "repository-item-link",
      "data-content-repository-attachable-id": attachableId,
      "data-type": itemType,
      "data-name": itemName,
      "data-repository-item-id": repositoryItemId,
      "data-path": itemPath,
    });

    const svgIcon = viewWriter.createRawElement(
      "svg",
      {
        class: "repository-item-link-svg",
        width: "18",
        height: "18",
        viewBox: "0 0 24 24",
        xmlns: "http://www.w3.org/2000/svg",
      },
      function (domElement) {
        domElement.innerHTML = activityIconPaths[itemType]
          ? activityIconPaths[itemType].reduce(
              (html, path) =>
                html + `<path d="${path}" fill="currentColor"></path>`,
              ""
            )
          : "";
      }
    );

    const itemLink = viewWriter.createRawElement(
      "a",
      {
        class: "repository-item-link-name",
        target: "_blank",
        href: itemPath,
      },
      function (domElement) {
        domElement.innerHTML = itemName;
      }
    );

    viewWriter.insert(viewWriter.createPositionAt(div, 0), svgIcon);
    viewWriter.insert(viewWriter.createPositionAt(div, "end"), itemLink);
    return toWidget(div, viewWriter, {
      label: "repository item link widget",
    });
  },
};

const upcastConversion = {
  model: (viewElement, { writer: modelWriter }) => {
    return modelWriter.createElement("repositoryItemLink", {
      "content-repository-attachable-id": viewElement.getAttribute(
        "data-content-repository-attachable-id"
      ),
      type: viewElement.getAttribute("data-type"),
      name: viewElement.getAttribute("data-name"),
      path: viewElement.getAttribute("data-path"),
      "repository-item-id": viewElement.getAttribute("data-repository-item-id"),
    });
  },
  view: {
    name: "div",
    classes: "repository-item-link",
  },
};

export default class RepositoryItemLink extends Plugin {
  static get requires() {
    return [RepositoryItemLinkEditing];
  }
}

class RepositoryItemLinkEditing extends Plugin {
  static get requires() {
    return [Widget];
  }

  init() {
    this._defineSchema();
    this._defineConverters();

    this.editor.commands.add(
      "insertRepositoryItemLink",
      new InsertRepositoryItemLinkCommand(this.editor)
    );

    this.editor.on("addRepositoryItemLinks", (eventInfo, repositoryItem) => {
      this.editor.execute("insertRepositoryItemLink", {
        repositoryItem,
      });
    });
  }

  _defineSchema() {
    const schema = this.editor.model.schema;

    schema.register("repositoryItemLink", {
      isObject: true,
      allowWhere: "$block",
      allowAttributes: [
        "content-repository-attachable-id",
        "type",
        "name",
        "repository-item-id",
        "path",
      ],
    });
  }

  _defineConverters() {
    const conversion = this.editor.conversion;

    conversion.for("upcast").elementToElement(upcastConversion);
    conversion.for("dataDowncast").elementToElement(downcastConversion);
    conversion.for("editingDowncast").elementToElement(downcastConversion);
  }
}

class InsertRepositoryItemLinkCommand extends Command {
  execute({ repositoryItem }) {
    this.editor.model.change((writer) => {
      const repositoryLink = createRepositoryItemLink(writer, repositoryItem);
      this.editor.model.insertContent(repositoryLink);
      this.editor.execute("insertParagraph", {
        position: this.editor.model.createPositionAfter(repositoryLink),
      });
    });
  }

  refresh() {
    const model = this.editor.model;
    const selection = model.document.selection;
    const allowedIn = model.schema.findAllowedParent(
      selection.getFirstPosition(),
      "repositoryItemLink"
    );

    this.isEnabled = allowedIn !== null;
  }
}

function createRepositoryItemLink(writer, repositoryItem) {
  const repositoryItemLink = writer.createElement("repositoryItemLink", {
    "content-repository-attachable-id":
      repositoryItem?.contentRepositoryAttachableId,
    type: repositoryItem?.type,
    name: repositoryItem?.name,
    "repository-item-id": repositoryItem?.id,
    path: repositoryItem?.path,
  });

  return repositoryItemLink;
}
